import _ from 'underscore';
import config from '@bingads-webui-component-legacy/config/index';
import { attachAdCustomizersHelpers as newAttachAdCustomizersHelpers } from '@bingads-webui-campaign/ad-customizers';

import { i18nDummy } from '@bingads-webui-campaign-legacy/crud-ad/service/utils/i18n-dummy-vnext';
import instrumentation from '@bingads-webui-campaign-legacy/instrumentation/index';
import edmCampaign from '@bingads-webui-component-legacy/edm-campaign/index';
import jsDataCampaign from '@bingads-webui-component-legacy/js-data-campaign/index';
import { getHelperProperties } from '@bingads-webui-campaign-legacy/crud-ad/service/adCustomizersUtils';
import { show as showAdCustomizersPicker, hide as hideAdCustomizersPicker, isVisible as isAdCustomizersPickerVisible } from '@bingads-webui-campaign-legacy/crud-ad/view/adsEditor/adCustomizersPicker';
import { show as showCountdownEditor, hide as hideCountdownEditor, isVisible as isCountdownEditorVisible } from '@bingads-webui-campaign-legacy/crud-ad/view/adsEditor/adCustomizersCountdownEditor';
import { show as showFeedEditor, hide as hideFeedEditor, isVisible as isFeedEditorVisible } from '@bingads-webui-campaign-legacy/crud-ad/view/adsEditor/feedsEditor';
import { showKeywordEditor, hideKeywordEditor, isKeywordEditorVisible } from '@bingads-webui-campaign/adcustomizers';
import { ADCUSTOMIZER_HELPER_PROPERTY_NAME, PICKER_HELPER_PROPERTY_NAME, KEYWORD_HELPER_PROPERTY_NAME, COUNTDOWN_HELPER_PROPERTY_NAME } from '@bingads-webui-campaign-legacy/crud-ad/service/utils/index';
import constants from '@bingads-webui-static-legacy/instrumentation/constants';
import logger from '@bingads-webui-static-legacy/instrumentation/index';

const i18nAdCustomizers = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/ad-customizers');

const dynamicAttachAdCustomizersHelpers = ({ container, element, updateAction, remainingCountCallBack, adType, field, perfMarker }) =>
  newAttachAdCustomizersHelpers({
    container,
    element,
    updateAction,
    remainingCountCallBack,
    deps: {
      i18n: i18nAdCustomizers,
      countdownI18nDummy: i18nDummy,
      config,
      instrumentation,
      edmCampaign,
      jsDataCampaign,
      perfMarker,
    },
    adType,
    field,
  });
/**
 * Logs message
 * @param {string} helper - helper associated with this log
 * @param {string} message - message to log
 * @returns {undefined}
 */

function log(helper, message) {
  logger.logMessage([constants.LOGCATEGORY.TRACE], constants.LOGLEVEL.INFO, helper, message);
}

function hidePickers(selectedPropertyType) {
  const hideFunctionMap = {
    [PICKER_HELPER_PROPERTY_NAME]: hideAdCustomizersPicker,
    [ADCUSTOMIZER_HELPER_PROPERTY_NAME]: hideFeedEditor,
    [COUNTDOWN_HELPER_PROPERTY_NAME]: hideCountdownEditor,
    [KEYWORD_HELPER_PROPERTY_NAME]: hideKeywordEditor,
  };

  const functinsToUse = _.omit(hideFunctionMap, selectedPropertyType);

  _.each(functinsToUse, (hideFunc) => {
    hideFunc();
  });
}

/**
 * Registers for click, focus, input and keyup events of given element to show appropriate ad customizer helpers
 * @param {object} element - element the helpers will be attaching to
 * @param {function} updateAction - callback function used to update the field helpers are attaching to
 * @param {function} remainingCountCallBack - callback function used to get the remainingcount
 * @returns {undefined}
 */
export function attachAdCustomizersHelpers(element, updateAction, remainingCountCallBack) {

  let isItemSelected = false;

  // Clicking on elements having this class won't hide currently visible helper
  element.addClass('ac-helper-area');

  // keyup is to catch typing, pasting with Ctrl+V, tab navigation, and cursor movement.
  // click is to catch all click events (including focus by clicking)
  // input is to catch paste with right click
  // focus is to catch programmatic focus
  element.on('input keyup focus click', (event) => {
    isItemSelected = event.target.isSelectedByUser;
    event.target.isSelectedByUser = false;
    // Ignore if any text is selected, we are only interested in the cursor
    if (event.target.selectionStart !== event.target.selectionEnd) {
      return;
    }

    // Ignore escape key up since it shouldn't prompt any helper
    if (event.type === 'keyup' && event.key === 'Escape') {
      return;
    }

    getHelperProperties(event.target.value, event.target.selectionEnd, isItemSelected).then((helperProperties) => {
      const selectdPropertyType = helperProperties == null ? '' : helperProperties.type;

      // hide the non-selected pickers
      hidePickers(selectdPropertyType);

      // updateaction when selectedPropertyType is not empty
      if (selectdPropertyType) {
        helperProperties.options.updateAction = _.wrap(updateAction, (func, param) => {
          log('AdCustomizersPicker', `${selectdPropertyType} item selected. Updating field value from "${event.target.value}" to "${param}"`);
          func(param);
        });
      }

      switch (selectdPropertyType) {
      case ADCUSTOMIZER_HELPER_PROPERTY_NAME:
        if (!isFeedEditorVisible()) {
          log('AdCustomizersPicker', `Showing feed for value: "${event.target.value}" and cursor position: ${event.target.selectionEnd}`);
        }
        showFeedEditor(event.target, helperProperties.options, remainingCountCallBack);
        break;

      case PICKER_HELPER_PROPERTY_NAME:
        if (!isAdCustomizersPickerVisible()) {
          log('AdCustomizersPicker', `Showing picker for value: "${event.target.value}" and cursor position: ${event.target.selectionEnd}`);
        }
        showAdCustomizersPicker(event.target, helperProperties.options, event.type === 'keyup' ? event.key : undefined);
        break;

      case COUNTDOWN_HELPER_PROPERTY_NAME:
        if (!isCountdownEditorVisible()) {
          log('AdCustomizersCountdownEditor', `Showing countdown editor for value: "${event.target.value}" and cursor position: ${event.target.selectionEnd}`);
        }
        showCountdownEditor(event.target, helperProperties.options);
        break;

      case KEYWORD_HELPER_PROPERTY_NAME:
        if (!isKeywordEditorVisible()) {
          log('AdCustomizersKeywordEditor', `Showing keyword editor for value: "${event.target.value}" and cursor position: ${event.target.selectionEnd}`);
        }
        showKeywordEditor(event.target, helperProperties.options, i18nAdCustomizers);
        break;
      default:
        break;
      }
    });
  });
}

export const enableAdCustomizersHelpers = (view, customizableFields, nameAttrs, adType, container) => {
  _.each(customizableFields, (field) => {
    const property = view.viewModel[field];
    const element = view.$(`.form-control[name="${nameAttrs[field]}"]`);

    dynamicAttachAdCustomizersHelpers(_.extend({
      element,
      updateAction: (text) => property(text),
      remainingCountCallBack: () => property.remaining(),
      adType,
      field,
      perfMarker: view.perfMarker,
    }, view.isForVNext ? { container: container ? container : view.$('> .ac-container') } : {}));
  });
};
